/**
 * Auth module exports
 */

import { isCapacitor } from '../api/config';
import { setStoredWorkspaceDomain } from '../utils/workspaceDomain';

export { AuthProvider, useAuth, AuthApiError } from './AuthContext';
export { AUTH_STORAGE_KEYS, EMULATION_STORAGE_KEYS } from './storageKeys';
export { authApi } from './authApi';
export { launchSSO, type LaunchResult } from './ssoLauncher';
export { demoApi, DemoApiError, type DemoVariation } from './demoApi';
export { hasDeveloperPortalAccess } from './types';
export {
  DEFAULT_RETURN_PATH,
  buildReturnPath,
  sanitizeReturnPath,
  getReturnPathFromState,
  portalHomePath,
  sanitizePortalReturnPath,
  getPortalReturnPathFromState,
  type ReturnUrlLocation,
} from './returnUrl';
export { enterDemo } from './enterDemo';
export {
  isDemoMode,
  getDemoVariation,
  setDemoMode,
  clearDemoMode,
} from './demoSession';
export type {
  User,
  Company,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RegisterWithInvitationRequest,
  VerifyEmailRequest,
  VerifyEmailResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  AuthState,
  AuthContextType,
  LoginOutcome,
  MfaMethod,
  MfaChallengeResponse,
  MfaStatus,
  MfaTrustedDevice,
  UserPreferences,
  NumberFormat,
  DateFormat,
  TimeFormat,
  SupportedLanguage,
} from './types';

/**
 * Adapt a cross-domain URL to match the current page's protocol and port.
 * Backend returns https:// URLs, but local dev uses http:// with custom ports.
 */
export function adaptCrossDomainUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.protocol = window.location.protocol;
    const { port } = window.location;
    if (port && port !== '80' && port !== '443') {
      parsed.port = port;
    } else {
      parsed.port = '';
    }
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return url;
  }
}

/**
 * The backend reported that the account lives on a specific tenant domain
 * (error code LOGIN_ON_CUSTOM_DOMAIN). Send the user to log in there.
 *
 * - **Web**: full-page navigation to that domain's login (carrying any query
 *   string, e.g. a prefilled email).
 * - **Native (Capacitor)**: the app runs from a single bundled origin
 *   (`capacitor://localhost` / `https://localhost`) and CANNOT navigate
 *   cross-origin — an `https://*.panluma.ai` navigation escapes to the system
 *   browser and would split storage across origins. Instead, re-point the
 *   app's API at that tenant by storing it as the workspace domain and reload
 *   the bundle in place; the user re-authenticates against the correct tenant
 *   entirely within the app. See docs/platform/mobile-offline-cold-start.md.
 */
export function redirectToCustomDomain(url: string): void {
  if (isCapacitor) {
    try {
      const target = new URL(url);
      setStoredWorkspaceDomain(target.host);
      window.location.href = `/login${target.search}`;
    } catch {
      window.location.href = '/login';
    }
    return;
  }
  window.location.href = adaptCrossDomainUrl(url);
}
